import React, { useState } from "react";
import { useRef } from "react";
import { Modal } from "react-bootstrap";
import "./index.css";
import { Order } from "../../../Payments/OrderRefund/ViewOrderRefund";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileExcel, faFilePdf } from "@fortawesome/free-solid-svg-icons";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import * as XLSX from "xlsx";
import { db } from "../../../../firebase";
import { collection, query, where, getDocs } from "firebase/firestore";

interface DeliveryPrintSetupModalProps {
  handleModalClose: () => void;
  showModal: boolean;
  orderList: Order[];
  cartName?: string;
  orderDate?: string;
  isAllCartsSelected?: boolean;
  isSummaryView?: boolean;
  menuItemCounts?: {
    name: string;
    totalCount: number;
    reservedCount: number;
  }[];
}

const DeliveryPrintSetupModal: React.FC<DeliveryPrintSetupModalProps> = ({
  showModal,
  handleModalClose,
  orderList,
  cartName,
  orderDate,
  isAllCartsSelected = false,
  isSummaryView = false,
  menuItemCounts = [],
}) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDownloadingExcel, setIsDownloadingExcel] = useState(false);
  const isDownloadingRef = useRef(false);
  const isDownloadingExcelRef = useRef(false);
  const formattedDate = orderDate ? orderDate.split("T")[0] : "";

  const getCustomerName = (order: any) => {
    if (order.orderCreatedBy === "CartPerson") return "Cart Person";
    if (order.orderCreatedBy === "Customer") return order.customer?.displayName || "Customer";
    if (order.orderCreatedBy === "Restaurant") {
      if (order.organization?.displayName) return order.organization.displayName;
      if (order.customer?.displayName) return order.customer.displayName;
      return "Restaurant";
    }
    return order.orderCreatedBy;
  };

  const getItemsSummary = (order: any) =>
    order.orderItems
      .map((item: any) => `${item.displayName} x ${item.quantity} (Rs. ${(item.unitPrice || 0).toFixed(2)})`)
      .join("; ");

  const getPaymentMethod = (order: any) => {
    if ((order as any).promoCode) return "Promo";
    return order.paymentMethod || "";
  };

  // Splits an array into chunks of a given size — Firestore's "in" operator
  // only accepts up to 10 values per query.
  const chunkArray = <T,>(arr: T[], size: number): T[][] => {
    const chunks: T[][] = [];
    for (let i = 0; i < arr.length; i += size) chunks.push(arr.slice(i, i + size));
    return chunks;
  };

  // Fetches the Transactions collection (a separate top-level collection,
  // linked to Orders only via the `orderId` field) and returns a map of
  // orderId -> transaction amount. When an order has more than one
  // Transaction record, a "Completed" one is preferred; otherwise whichever
  // is encountered first is kept.
  const fetchTransactionAmountsByOrderId = async (
    orderIds: string[]
  ): Promise<Record<string, number>> => {
    const best: Record<string, { amount: number; status?: string }> = {};
    const uniqueIds = Array.from(new Set(orderIds.filter(Boolean)));
    const chunks = chunkArray(uniqueIds, 10);

    for (const chunk of chunks) {
      if (chunk.length === 0) continue;
      const q = query(collection(db, "Transactions"), where("orderId", "in", chunk));
      const snap = await getDocs(q);
      snap.forEach((docSnap) => {
        const data: any = docSnap.data();
        const oid = data.orderId;
        const amount = typeof data.amount === "number" ? data.amount : parseFloat(data.amount);
        if (!oid || Number.isNaN(amount)) return;

        const isCompleted = data.status === "Completed";
        if (!best[oid] || (isCompleted && best[oid].status !== "Completed")) {
          best[oid] = { amount, status: data.status };
        }
      });
    }

    const result: Record<string, number> = {};
    Object.entries(best).forEach(([oid, v]) => {
      result[oid] = v.amount;
    });
    return result;
  };

  // Returns the transaction amount only when it differs from the order's
  // net amount (beyond floating-point rounding); otherwise returns "" so
  // the Excel cell stays blank for orders that reconcile correctly.
  const getTransactionMismatch = (
    order: any,
    transactionAmountsByOrderId: Record<string, number>
  ) => {
    const txAmount = transactionAmountsByOrderId[order.id];
    if (txAmount === undefined) return "";
    const netAmount = order.paymentSummary?.netAmount || 0;
    if (Math.abs(txAmount - netAmount) < 0.01) return "";
    return parseFloat(txAmount.toFixed(2));
  };

  // Filter out cancelled and tentative orders (case-insensitive)
  const filteredOrders = orderList.filter((order) => {
    const status = (order.orderStatus || "").toString().toLowerCase();
    return status !== "cancelled" && status !== "tentative";
  });

  // Group orders by cart when "All" is selected (excluding Restaurant orders)
  const groupedOrders = isAllCartsSelected
    ? filteredOrders.reduce((acc: any, order) => {
        const cartDisplayName =
          order.cart?.displayName ||
          (order.orderCreatedBy === "Restaurant"
            ? "Restaurant Orders"
            : "Unknown Cart");
        if (!acc[cartDisplayName]) {
          acc[cartDisplayName] = [];
        }
        acc[cartDisplayName].push(order);
        return acc;
      }, {})
    : null;

  const handleDownloadExcel = async () => {
    if (isDownloadingExcelRef.current) return;
      isDownloadingExcelRef.current = true;
    setIsDownloadingExcel(true);
    try {
      const wb = XLSX.utils.book_new();

      let transactionAmountsByOrderId: Record<string, number> = {};
          if (!isSummaryView) {
            try {
              transactionAmountsByOrderId = await fetchTransactionAmountsByOrderId(
                filteredOrders.map((o) => o.id)
              );
            } catch (txError) {
              console.error("Error fetching transaction amounts:", txError);
              // Export still proceeds — the new column just stays blank for every row.
            }
          }

      if (isSummaryView) {
        const headerRow = ["#", "Menu Item", "Total Quantity", "Reserved Items", "Available Quantity"];
        const dataRows = menuItemCounts.map((item, index) => [
          index + 1, item.name, item.totalCount, item.reservedCount,
          item.totalCount - item.reservedCount,
        ]);
        const totalRow = [
          "", "Total Items:",
          menuItemCounts.reduce((s, i) => s + i.totalCount, 0),
          menuItemCounts.reduce((s, i) => s + i.reservedCount, 0),
          menuItemCounts.reduce((s, i) => s + (i.totalCount - i.reservedCount), 0),
        ];
        const ws = XLSX.utils.aoa_to_sheet([headerRow, ...dataRows, totalRow]);
        ws["!cols"] = [{ wch: 5 }, { wch: 35 }, { wch: 16 }, { wch: 16 }, { wch: 20 }];
        XLSX.utils.book_append_sheet(wb, ws, (isAllCartsSelected ? "All Carts Summary" : cartName || "Summary").slice(0, 31));
        XLSX.writeFile(wb, `Menu_Item_Summary_${formattedDate}.xlsx`);

      } else if (isAllCartsSelected && groupedOrders) {
        Object.entries(groupedOrders).forEach(([cartDisplayName, orders]: [string, any]) => {
          const headerRow = ["#", "Order Reference", "Customer", "Mobile Number", "Address", "Delivery Cart", "Items Ordered", "Order Value (Rs.)", "Delivery Fee (Rs.)", "Discount (Rs.)", "Net Amount (Rs.)", "Transaction Amount (Rs.)", "Payment Method", "Agent", "Issued"];
          const dataRows = orders.map((order: any, index: number) => [
            index + 1, order.orderReference, getCustomerName(order),
            order.customer?.phoneNumber || "N/A",
            order.address || "No address provided",
            order.deliveryCart?.displayName || "Unassigned",
            getItemsSummary(order),
            parseFloat((order.paymentSummary?.totalPrice || 0).toFixed(2)),
            order.paymentSummary?.isDeliveryFree
              ? `FREE (\u0336${(order.paymentSummary?.deliveryFee || 0).toFixed(2).split("").join("\u0336")}\u0336)`
              : parseFloat((order.paymentSummary?.deliveryFee || 0).toFixed(2)),
            parseFloat((order.paymentSummary?.discount || 0).toFixed(2)),
            parseFloat((order.paymentSummary?.netAmount || 0).toFixed(2)),
            getTransactionMismatch(order, transactionAmountsByOrderId),
            (order as any).promoCode ? "Promo" : (order.paymentMethod || ""),
            order.orderTakenBy?.displayName || "",
            "",
          ]);
          const totalRow = ["", "", "", "", "", "", "Total:",
            parseFloat(orders.reduce((s: number, o: any) => s + (o.paymentSummary?.totalPrice || 0), 0).toFixed(2)),
            parseFloat(orders.reduce((s: number, o: any) => s + (o.paymentSummary?.isDeliveryFree ? 0 : (o.paymentSummary?.deliveryFee || 0)), 0).toFixed(2)),
            parseFloat(orders.reduce((s: number, o: any) => s + (o.paymentSummary?.discount || 0), 0).toFixed(2)),
            parseFloat(orders.reduce((s: number, o: any) => s + (o.paymentSummary?.netAmount || 0), 0).toFixed(2)),
            "", "", "", ""];
          const ws = XLSX.utils.aoa_to_sheet([headerRow, ...dataRows, totalRow]);
          ws["!cols"] = [{ wch: 5 }, { wch: 18 }, { wch: 15 }, { wch: 16 }, { wch: 30 }, { wch: 24 }, { wch: 40 }, { wch: 16 }, { wch: 16 }, { wch: 16 }, { wch: 16 }, { wch: 18 }, { wch: 18 }, { wch: 20 }, { wch: 8 }];
          XLSX.utils.book_append_sheet(wb, ws, cartDisplayName.slice(0, 31));
        });
        XLSX.writeFile(wb, `Delivery_Orders_${formattedDate}.xlsx`);

      } else {
        const headerRow = ["#", "Order Reference", "Customer", "Mobile Number", "Address", "Delivery Cart", "Items Ordered", "Order Value (Rs.)", "Delivery Fee (Rs.)", "Discount (Rs.)", "Net Amount (Rs.)", "Transaction Amount (Rs.)", "Payment Method", "Agent", "Issued"];
        const dataRows = filteredOrders.map((order: any, index: number) => [
          index + 1, order.orderReference, getCustomerName(order),
          order.customer?.phoneNumber || "N/A",
          order.address || "No address provided",
          order.deliveryCart?.displayName || "Unassigned",
          getItemsSummary(order),
          parseFloat((order.paymentSummary?.totalPrice || 0).toFixed(2)),
          order.paymentSummary?.isDeliveryFree
            ? `FREE (\u0336${(order.paymentSummary?.deliveryFee || 0).toFixed(2).split("").join("\u0336")}\u0336)`
            : parseFloat((order.paymentSummary?.deliveryFee || 0).toFixed(2)),
          parseFloat((order.paymentSummary?.discount || 0).toFixed(2)),
          parseFloat((order.paymentSummary?.netAmount || 0).toFixed(2)),
          getTransactionMismatch(order, transactionAmountsByOrderId),
          (order as any).promoCode ? "Promo" : (order.paymentMethod || ""),
          (order as any).orderTakenBy?.displayName || "",
          "",
        ]);
        const totalRow = ["", "", "", "", "", "", "Total:",
          parseFloat(filteredOrders.reduce((s: number, o: any) => s + (o.paymentSummary?.totalPrice || 0), 0).toFixed(2)),
          parseFloat(filteredOrders.reduce((s: number, o: any) => s + (o.paymentSummary?.isDeliveryFree ? 0 : (o.paymentSummary?.deliveryFee || 0)), 0).toFixed(2)),
          parseFloat(filteredOrders.reduce((s: number, o: any) => s + (o.paymentSummary?.discount || 0), 0).toFixed(2)),
          parseFloat(filteredOrders.reduce((s: number, o: any) => s + (o.paymentSummary?.netAmount || 0), 0).toFixed(2)),
          "", "", "", ""];
        const ws = XLSX.utils.aoa_to_sheet([headerRow, ...dataRows, totalRow]);
        ws["!cols"] = [{ wch: 5 }, { wch: 18 }, { wch: 15 }, { wch: 16 }, { wch: 30 }, { wch: 24 }, { wch: 40 }, { wch: 16 }, { wch: 16 }, { wch: 16 }, { wch: 16 }, { wch: 18 }, { wch: 18 }, { wch: 20 }, { wch: 8 }];
        XLSX.utils.book_append_sheet(wb, ws, (cartName || "Orders").slice(0, 31));
        XLSX.writeFile(wb, `${cartName}_Orders_${formattedDate}.xlsx`);
      }
    } catch (error) {
      console.error("Error generating Excel:", error);
    } finally {
      isDownloadingExcelRef.current = false;
      setIsDownloadingExcel(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (isDownloadingRef.current) return;
    isDownloadingRef.current = true;
    setIsDownloading(true);

    try {
      const pdf = isSummaryView
        ? new jsPDF("p", "mm", "a4")   // summary stays portrait
        : new jsPDF("l", "mm", "a4");  // order views use landscape
      const pdfWidth = pdf.internal.pageSize.getWidth();

      if (isSummaryView) {
        // Handle summary view printing (same as before)
        const tempDiv = document.createElement("div");
        tempDiv.style.position = "absolute";
        tempDiv.style.left = "-9999px";
        tempDiv.style.width = "210mm";
        tempDiv.style.padding = "20px";
        tempDiv.style.backgroundColor = "white";

        tempDiv.innerHTML = `
  <div style="text-align: center; margin-bottom: 20px; font-size: 18px; font-weight: bold;">
    Menu Item Summary ${
      isAllCartsSelected ? "For All Carts" : cartName ? `For ${cartName}` : ""
    } on ${formattedDate}
  </div>
  <table style="width: 100%; border-collapse: collapse; font-size: 12px;">
    <thead>
      <tr style="background-color: #f8f9fa;">
        <th style="border: 1px solid #dee2e6; padding: 8px; text-align: left;">#</th>
        <th style="border: 1px solid #dee2e6; padding: 8px; text-align: left;">Menu Item</th>
        <th style="border: 1px solid #dee2e6; padding: 8px; text-align: center;">Total Quantity</th>
        <th style="border: 1px solid #dee2e6; padding: 8px; text-align: center;">Reserved Items</th>
        <th style="border: 1px solid #dee2e6; padding: 8px; text-align: center;">Available Quantity</th>
      </tr>
    </thead>
    <tbody>
      ${menuItemCounts
        .map(
          (item, index) => `
        <tr>
          <td style="border: 1px solid #dee2e6; padding: 8px;">${index + 1}</td>
          <td style="border: 1px solid #dee2e6; padding: 8px;">${item.name}</td>
          <td style="border: 1px solid #dee2e6; padding: 8px; text-align: center;">${
            item.totalCount
          }</td>
          <td style="border: 1px solid #dee2e6; padding: 8px; text-align: center;">${
            item.reservedCount
          }</td>
          <td style="border: 1px solid #dee2e6; padding: 8px; text-align: center;">${
            item.totalCount - item.reservedCount
          }</td>
        </tr>
      `
        )
        .join("")}
      <tr style="font-weight: bold;">
        <td colspan="2" style="border: 1px solid #dee2e6; padding: 8px; text-align: right;">Total Items:</td>
        <td style="border: 1px solid #dee2e6; padding: 8px; text-align: center;">
          ${menuItemCounts.reduce((sum, item) => sum + item.totalCount, 0)}
        </td>
        <td style="border: 1px solid #dee2e6; padding: 8px; text-align: center;">
          ${menuItemCounts.reduce((sum, item) => sum + item.reservedCount, 0)}
        </td>
        <td style="border: 1px solid #dee2e6; padding: 8px; text-align: center;">
          ${menuItemCounts.reduce(
            (sum, item) => sum + (item.totalCount - item.reservedCount),
            0
          )}
        </td>
      </tr>
    </tbody>
  </table>
`;

        document.body.appendChild(tempDiv);

        const canvas = await html2canvas(tempDiv, {
          scale: 2,
          useCORS: true,
          allowTaint: true,
        });

        const imgData = canvas.toDataURL("image/png");
        const imgProps = pdf.getImageProperties(imgData);
        const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

        pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
        document.body.removeChild(tempDiv);

        pdf.save(`Menu_Item_Summary_${formattedDate}.pdf`);
      } else if (isAllCartsSelected && groupedOrders) {
        // Generate separate pages for each cart in order view WITH MOBILE & ADDRESS
        const cartNames = Object.keys(groupedOrders);

        for (let cartIndex = 0; cartIndex < cartNames.length; cartIndex++) {
          const cartDisplayName = cartNames[cartIndex];
          const orders = groupedOrders[cartDisplayName];
          const ordersPerPage = 19;
          const orderChunks = [];
          for (let i = 0; i < orders.length; i += ordersPerPage) {
            orderChunks.push(orders.slice(i, i + ordersPerPage));
          }

          for (
            let chunkIndex = 0;
            chunkIndex < orderChunks.length;
            chunkIndex++
          ) {
            const currentOrders = orderChunks[chunkIndex];
            const isFirstChunk = chunkIndex === 0;
            const isLastChunk = chunkIndex === orderChunks.length - 1;

            // Create a temporary div for each page
            const tempDiv = document.createElement("div");
            tempDiv.style.position = "fixed";
            tempDiv.style.top = "-9999px";
            tempDiv.style.left = "-9999px";
            tempDiv.style.width = "297mm"; // Increased width for landscape or use a4 landscape
            tempDiv.style.padding = "15px";
            tempDiv.style.backgroundColor = "white";
            tempDiv.style.fontSize = "10px"; // Smaller font to fit more columns

            // Header (only show on first page for each cart)
            const header = isFirstChunk
              ? `
        <div style="text-align: center; margin-bottom: 15px; font-size: 16px; font-weight: bold;">
          Order Summary For ${cartDisplayName} on ${formattedDate}
        </div>
      `
              : "";

            // Table header with mobile and address columns
            const tableHeader = `
        <table style="width: 100%; border-collapse: collapse; font-size: 9px; table-layout: fixed;">
          <thead>
            <tr style="background-color: #f8f9fa;">
              <th style="border: 1px solid #dee2e6; padding: 6px; text-align: left; width: 5%;">#</th>
              <th style="border: 1px solid #dee2e6; padding: 6px; text-align: left; width: 12%;">Order Ref</th>
              <th style="border: 1px solid #dee2e6; padding: 6px; text-align: left; width: 10%;">Customer</th>
              <th style="border: 1px solid #dee2e6; padding: 6px; text-align: left; width: 15%;">Mobile</th>
              <th style="border: 1px solid #dee2e6; padding: 6px; text-align: left; width: 20%;">Address</th>
              <th style="border: 1px solid #dee2e6; padding: 6px; text-align: left; width: 18%;">Items Ordered</th>
              <th style="border: 1px solid #dee2e6; padding: 6px; text-align: left; width: 8%;">Order Value</th>
              <th style="border: 1px solid #dee2e6; padding: 6px; text-align: left; width: 8%;">Delivery Fee</th>
              <th style="border: 1px solid #dee2e6; padding: 6px; text-align: left; width: 8%;">Discount</th>
              <th style="border: 1px solid #dee2e6; padding: 6px; text-align: left; width: 8%;">Net Amount</th>
              <th style="border: 1px solid #dee2e6; padding: 6px; text-align: left; width: 8%;">Issued</th>
            </tr>
          </thead>
          <tbody>
      `;

            // Table footer (only show on last page for each cart)
            const tableFooter = isLastChunk
              ? `
                <tr style="font-weight: bold;">
                  <td colspan="6" style="border: 1px solid #dee2e6; padding: 6px; text-align: right;">Total:</td>
                  <td style="border: 1px solid #dee2e6; padding: 6px;">Rs. ${orders.reduce((sum: number, o: any) => sum + (o.paymentSummary?.totalPrice || 0), 0).toFixed(2)}</td>
                  <td style="border: 1px solid #dee2e6; padding: 6px;">Rs. ${orders.reduce((sum: number, o: any) => sum + (o.paymentSummary?.isDeliveryFree ? 0 : (o.paymentSummary?.deliveryFee || 0)), 0).toFixed(2)}</td>
                  <td style="border: 1px solid #dee2e6; padding: 6px;">Rs. ${orders.reduce((sum: number, o: any) => sum + (o.paymentSummary?.discount || 0), 0).toFixed(2)}</td>
                  <td style="border: 1px solid #dee2e6; padding: 6px;">Rs. ${orders.reduce((sum: number, o: any) => sum + (o.paymentSummary?.netAmount || 0), 0).toFixed(2)}</td>
                  <td style="border: 1px solid #dee2e6; padding: 6px;"></td>
                </tr>
          </tbody>
        </table>
      `
              : `
          </tbody>
        </table>
      `;

            // Build table rows with continuous numbering and new columns
            const tableRows = currentOrders
              .map(
                (order: any, index: number) => `
            <tr>
              <td style="border: 1px solid #dee2e6; padding: 6px; word-wrap: break-word;">${
                chunkIndex * ordersPerPage + index + 1
              }</td>
              <td style="border: 1px solid #dee2e6; padding: 6px; word-wrap: break-word;">${
                order.orderReference
              }</td>
              <td style="border: 1px solid #dee2e6; padding: 6px; word-wrap: break-word;">
                ${getCustomerName(order)}
              </td>
              <td style="border: 1px solid #dee2e6; padding: 6px; word-wrap: break-word;">
                ${order.customer?.phoneNumber || "N/A"}
              </td>
              <td style="border: 1px solid #dee2e6; padding: 6px; word-wrap: break-word;">
                ${order.address || "No address provided"}
              </td>
              <td style="border: 1px solid #dee2e6; padding: 6px; word-wrap: break-word;">
                ${order.orderItems
                  .map(
                    (item: any) =>
                      `${item.displayName} x ${
                        item.quantity
                      } (Rs. ${(item.unitPrice || 0).toFixed(2)})`
                  )
                  .join("<br>")}
              </td>
              <td style="border: 1px solid #dee2e6; padding: 6px; word-wrap: break-word;">Rs. ${(order.paymentSummary?.totalPrice || 0).toFixed(2)}</td>
              <td style="border: 1px solid #dee2e6; padding: 6px; word-wrap: break-word;">
                ${order.paymentSummary?.isDeliveryFree
                  ? `<span style="color:#28a745;font-weight:bold;">FREE</span> <s style="color:#999;">Rs. ${(order.paymentSummary?.deliveryFee || 0).toFixed(2)}</s>`
                  : `Rs. ${(order.paymentSummary?.deliveryFee || 0).toFixed(2)}`
                }
              </td>
              <td style="border: 1px solid #dee2e6; padding: 6px; word-wrap: break-word;">Rs. ${(order.paymentSummary?.discount || 0).toFixed(2)}</td>
              <td style="border: 1px solid #dee2e6; padding: 6px; word-wrap: break-word;">Rs. ${(order.paymentSummary?.netAmount || 0).toFixed(2)}</td>
              <td style="border: 1px solid #dee2e6; padding: 6px; word-wrap: break-word;"></td>
            </tr>
          `
              )
              .join("");

            // Combine all parts
            tempDiv.innerHTML = header + tableHeader + tableRows + tableFooter;
            document.body.appendChild(tempDiv);

            // Capture the page content
            const canvas = await html2canvas(tempDiv, {
              scale: 2,
              useCORS: true,
              allowTaint: true,
            });

            const imgData = canvas.toDataURL("image/png");
            const imgProps = pdf.getImageProperties(imgData);
            const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

            // Add new page if not the first page
            if (cartIndex > 0 || chunkIndex > 0) {
              pdf.addPage();
            }

            pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
            document.body.removeChild(tempDiv);
          }
        }

        pdf.save(`Delivery_Orders_${formattedDate}.pdf`);
      } else {
        // Handle single cart with proper pagination WITH MOBILE & ADDRESS
        const tempDiv = document.createElement("div");
        tempDiv.style.position = "fixed";
        tempDiv.style.top = "-9999px";
        tempDiv.style.left = "-9999px";
        tempDiv.style.width = "297mm";
        tempDiv.style.padding = "15px";
        tempDiv.style.backgroundColor = "white";
        tempDiv.style.fontSize = "10px";

        // Create the header
        const header = `
        <div style="text-align: center; margin-bottom: 15px; font-size: 16px; font-weight: bold;">
          Order Summary For ${cartName} on ${formattedDate}
        </div>
      `;

        // Create the table header with mobile and address
        const tableHeader = `
        <table style="width: 100%; border-collapse: collapse; font-size: 9px; table-layout: fixed; margin-bottom: 15px;">
          <thead>
            <tr style="background-color: #f8f9fa;">
              <th style="border: 1px solid #dee2e6; padding: 6px; text-align: left; width: 5%;">#</th>
              <th style="border: 1px solid #dee2e6; padding: 6px; text-align: left; width: 12%;">Order Ref</th>
              <th style="border: 1px solid #dee2e6; padding: 6px; text-align: left; width: 10%;">Customer</th>
              <th style="border: 1px solid #dee2e6; padding: 6px; text-align: left; width: 15%;">Mobile</th>
              <th style="border: 1px solid #dee2e6; padding: 6px; text-align: left; width: 20%;">Address</th>
              <th style="border: 1px solid #dee2e6; padding: 6px; text-align: left; width: 18%;">Items Ordered</th>
              <th style="border: 1px solid #dee2e6; padding: 6px; text-align: left; width: 8%;">Order Value</th>
              <th style="border: 1px solid #dee2e6; padding: 6px; text-align: left; width: 8%;">Delivery Fee</th>
              <th style="border: 1px solid #dee2e6; padding: 6px; text-align: left; width: 8%;">Discount</th>
              <th style="border: 1px solid #dee2e6; padding: 6px; text-align: left; width: 8%;">Net Amount</th>
              <th style="border: 1px solid #dee2e6; padding: 6px; text-align: left; width: 8%;">Issued</th>
            </tr>
          </thead>
          <tbody>
      `;

        // Create the table footer with totals
        const tableFooter = `
            <tr style="font-weight: bold;">
              <td colspan="6" style="border: 1px solid #dee2e6; padding: 6px; text-align: right;">Total:</td>
              <td style="border: 1px solid #dee2e6; padding: 6px;">Rs. ${filteredOrders.reduce((sum: number, o: any) => sum + (o.paymentSummary?.totalPrice || 0), 0).toFixed(2)}</td>
              <td style="border: 1px solid #dee2e6; padding: 6px;">Rs. ${filteredOrders.reduce((sum: number, o: any) => sum + (o.paymentSummary?.isDeliveryFree ? 0 : (o.paymentSummary?.deliveryFee || 0)), 0).toFixed(2)}</td>
              <td style="border: 1px solid #dee2e6; padding: 6px;">Rs. ${filteredOrders.reduce((sum: number, o: any) => sum + (o.paymentSummary?.discount || 0), 0).toFixed(2)}</td>
              <td style="border: 1px solid #dee2e6; padding: 6px;">Rs. ${filteredOrders.reduce((sum: number, o: any) => sum + (o.paymentSummary?.netAmount || 0), 0).toFixed(2)}</td>
              <td style="border: 1px solid #dee2e6; padding: 6px;"></td>
            </tr>
          </tbody>
        </table>
      `;

        // Split orders into chunks to prevent overflow
        const ordersPerPage = 10; // Reduced due to additional columns
        const orderChunks = [];
        for (let i = 0; i < filteredOrders.length; i += ordersPerPage) {
          orderChunks.push(filteredOrders.slice(i, i + ordersPerPage));
        }

        for (let i = 0; i < orderChunks.length; i++) {
          const orders = orderChunks[i];

          // Build the table rows for this chunk
          const tableRows = orders
            .map(
              (order, index) => `
              <tr>
                <td style="border: 1px solid #dee2e6; padding: 6px; word-wrap: break-word;">${
                  i * ordersPerPage + index + 1
                }</td>
                <td style="border: 1px solid #dee2e6; padding: 6px; word-wrap: break-word;">${
                  order.orderReference
                }</td>
                <td style="border: 1px solid #dee2e6; padding: 6px; word-wrap: break-word;">
                  ${getCustomerName(order)}
                </td>
                <td style="border: 1px solid #dee2e6; padding: 6px; word-wrap: break-word;">
                  ${order.customer?.phoneNumber || "N/A"}
                </td>
                <td style="border: 1px solid #dee2e6; padding: 6px; word-wrap: break-word;">
                  ${order.address || "No address provided"}
                </td>
                <td style="border: 1px solid #dee2e6; padding: 6px; word-wrap: break-word;">
                  ${order.orderItems
                    .map(
                      (item: any) =>
                        `${item.displayName} x ${
                          item.quantity
                        } (Rs. ${(item.unitPrice || 0).toFixed(2)})`
                    )
                    .join("<br>")}
                </td>
                <td style="border: 1px solid #dee2e6; padding: 6px; word-wrap: break-word;">Rs. ${(order.paymentSummary?.totalPrice || 0).toFixed(2)}</td>
                <td style="border: 1px solid #dee2e6; padding: 6px; word-wrap: break-word;">
                  ${order.paymentSummary?.isDeliveryFree
                    ? `<s style="color:#999;">Rs. ${(order.paymentSummary?.deliveryFee || 0).toFixed(2)}</s>`
                    : `Rs. ${(order.paymentSummary?.deliveryFee || 0).toFixed(2)}`
                  }
                </td>
                <td style="border: 1px solid #dee2e6; padding: 6px; word-wrap: break-word;">Rs. ${(order.paymentSummary?.discount || 0).toFixed(2)}</td>
                <td style="border: 1px solid #dee2e6; padding: 6px; word-wrap: break-word;">Rs. ${(order.paymentSummary?.netAmount || 0).toFixed(2)}</td>
                <td style="border: 1px solid #dee2e6; padding: 6px; word-wrap: break-word;"></td>
              </tr>
            `
            )
            .join("");

          // Combine all parts for this page
          tempDiv.innerHTML =
            header +
            tableHeader +
            tableRows +
            (i === orderChunks.length - 1 ? tableFooter : "</tbody></table>");

          document.body.appendChild(tempDiv);

          const canvas = await html2canvas(tempDiv, {
            scale: 2,
            useCORS: true,
            allowTaint: true,
          });

          const imgData = canvas.toDataURL("image/png");
          const imgProps = pdf.getImageProperties(imgData);
          const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

          // Add new page for each chunk (except the first one)
          if (i > 0) {
            pdf.addPage();
          }

          pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);

          // Clean up
          document.body.removeChild(tempDiv);
        }
        pdf.save(`${cartName}_Orders_${formattedDate}.pdf`);
      }
      setIsDownloading(false);
    } catch (error) {
          console.error("Error generating PDF:", error);
        } finally {
          isDownloadingRef.current = false;
          setIsDownloading(false);
        }
  };

  return (
    <Modal
      show={showModal}
      onHide={handleModalClose}
      backdrop="static"
      dialogClassName="modal-95w"
      centered
    >
      <Modal.Header closeButton>
        <div className="d-flex justify-content-end align-items-center gap-3 w-100">
          <button
            className="btn btn-sm btn-success d-flex align-items-center gap-1"
            onClick={!isDownloadingExcel ? handleDownloadExcel : undefined}
            disabled={isDownloadingExcel}
            title="Download as Excel"
            style={{ opacity: isDownloadingExcel ? 0.6 : 1 }}
          >
            <FontAwesomeIcon icon={faFileExcel} />
            <span>{isDownloadingExcel ? "Downloading..." : "Excel"}</span>
          </button>
          <button
            className="btn btn-sm btn-danger d-flex align-items-center gap-1"
            onClick={!isDownloading ? handleDownloadPdf : undefined}
            disabled={isDownloading}
            title="Download as PDF"
            style={{ opacity: isDownloading ? 0.6 : 1 }}
          >
            <FontAwesomeIcon icon={faFilePdf} />
            <span>{isDownloading ? "Downloading..." : "PDF"}</span>
          </button>
        </div>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
        <div id="printable-area">
          {isSummaryView ? (
            // Summary view content (unchanged)
            <>
              <div className="header-text-pop-up text-center mb-3">
                {isAllCartsSelected
                  ? `Menu Item Summary For All Carts on ${formattedDate}`
                  : `Menu Item Summary For ${cartName} on ${formattedDate}`}
              </div>
              <div style={{ overflowX: "auto" }}>
                <table className="table table-bordered table-sm">
                  <thead className="table-light">
                    <tr>
                      <th>#</th>
                      <th>Menu Item</th>
                      <th className="text-center">Total Quantity</th>
                      <th className="text-center">Reserved Items</th>
                      <th className="text-center">Available Quantity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {menuItemCounts.map((item, index) => (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        <td>{item.name}</td>
                        <td className="text-center">{item.totalCount}</td>
                        <td className="text-center">{item.reservedCount}</td>
                        <td className="text-center">
                          {item.totalCount - item.reservedCount}
                        </td>
                      </tr>
                    ))}
                    <tr className="fw-bold">
                      <td colSpan={2} className="text-end">
                        Total Items:
                      </td>
                      <td className="text-center">
                        {menuItemCounts.reduce(
                          (sum, item) => sum + item.totalCount,
                          0
                        )}
                      </td>
                      <td className="text-center">
                        {menuItemCounts.reduce(
                          (sum, item) => sum + item.reservedCount,
                          0
                        )}
                      </td>
                      <td className="text-center">
                        {menuItemCounts.reduce(
                          (sum, item) =>
                            sum + (item.totalCount - item.reservedCount),
                          0
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </>
          ) : isAllCartsSelected && groupedOrders ? (
            // Grouped order view content WITH MOBILE & ADDRESS
            <>
              {Object.entries(groupedOrders).map(
                ([cartDisplayName, orders]: [string, any]) => (
                  <div key={cartDisplayName} className="mb-4">
                    <h5 className="fw-bold text-primary mb-3">
                      {cartDisplayName}
                    </h5>
                    <div style={{ overflowX: "auto" }}>
                      <table className="table table-bordered table-sm mb-4">
                        <thead className="table-light">
                          <tr>
                            <th>#</th>
                            <th>Order Reference</th>
                            <th>Customer</th>
                            <th>Mobile Number</th>
                            <th>Address</th>
                            <th>Items Ordered</th>
                            <th>Order Value</th>
                            <th>Delivery Fee</th>
                            <th>Discount</th>
                            <th>Net Amount</th>
                            <th>Payment Method</th>
                            <th>Agent</th>
                            <th>Issued</th>
                          </tr>
                        </thead>
                        <tbody>
                          {orders.map((order: any, index: number) => (
                            <tr key={order.id}>
                              <td>{index + 1}</td>
                              <td>{order.orderReference}</td>
                              <td>
                                {getCustomerName(order)}
                              </td>
                              <td>{order.customer?.phoneNumber || "N/A"}</td>
                              <td>{order.address || "No address provided"}</td>
                              <td>
                                {order.orderItems.map((item: any, i: number) => (
                                  <div key={i}>
                                    {item.displayName} x {item.quantity} ( Rs.{" "}
                                    {(item.unitPrice || 0).toFixed(2)})
                                  </div>
                                ))}
                              </td>
                              <td>Rs. {(order.paymentSummary?.totalPrice || 0).toFixed(2)}</td>
                              <td>
                                {order.paymentSummary?.isDeliveryFree ? (
                                  <s className="text-muted">Rs. {(order.paymentSummary?.deliveryFee || 0).toFixed(2)}</s>
                                ) : (
                                  <>Rs. {(order.paymentSummary?.deliveryFee || 0).toFixed(2)}</>
                                )}
                              </td>
                              <td>Rs. {(order.paymentSummary?.discount || 0).toFixed(2)}</td>
                              <td>Rs. {(order.paymentSummary?.netAmount || 0).toFixed(2)}</td>
                              <td>{getPaymentMethod(order)}</td>
                              <td>{(order as any).orderTakenBy?.displayName || ""}</td>
                              <td></td>
                            </tr>
                          ))}
                          <tr className="fw-bold">
                            <td colSpan={6} className="text-end">Total:</td>
                            <td>Rs. {orders.reduce((sum: number, o: any) => sum + (o.paymentSummary?.totalPrice || 0), 0).toFixed(2)}</td>
                            <td>Rs. {orders.reduce((sum: number, o: any) => sum + (o.paymentSummary?.isDeliveryFree ? 0 : (o.paymentSummary?.deliveryFee || 0)), 0).toFixed(2)}</td>
                            <td>Rs. {orders.reduce((sum: number, o: any) => sum + (o.paymentSummary?.discount || 0), 0).toFixed(2)}</td>
                            <td>Rs. {orders.reduce((sum: number, o: any) => sum + (o.paymentSummary?.netAmount || 0), 0).toFixed(2)}</td>
                            <td></td>
                            <td></td>
                            <td></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                )
              )}
            </>
          ) : (
            // Single cart order view content WITH MOBILE & ADDRESS
            <>
              <div className="header-text-pop-up text-center mb-3">
                {`Order Summary For ${cartName} on ${formattedDate}`}
              </div>
              <div style={{ overflowX: "auto" }}>
                <table className="table table-bordered table-sm">
                  <thead className="table-light">
                    <tr>
                      <th>#</th>
                      <th>Order Reference</th>
                      <th>Customer</th>
                      <th>Mobile Number</th>
                      <th>Address</th>
                      <th>Items Ordered</th>
                      <th>Order Value</th>
                      <th>Delivery Fee</th>
                      <th>Discount</th>
                      <th>Net Amount</th>
                      <th>Payment Method</th>
                      <th>Agent</th>
                      <th>Issued</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOrders.map((order, index) => (
                      <tr key={order.id}>
                        <td>{index + 1}</td>
                        <td>{order.orderReference}</td>
                        <td>
                          {getCustomerName(order)}
                        </td>
                        <td>{order.customer?.phoneNumber || "N/A"}</td>
                        <td>{order.address || "No address provided"}</td>
                        <td>
                          {order.orderItems.map((item, i) => (
                            <div key={i}>
                              {item.displayName} x {item.quantity} ( Rs.{" "}
                              {(item.unitPrice || 0).toFixed(2)})
                            </div>
                          ))}
                        </td>
                        <td>Rs. {(order.paymentSummary?.totalPrice || 0).toFixed(2)}</td>
                        <td>
                          {order.paymentSummary?.isDeliveryFree ? (
                            <s className="text-muted">Rs. {(order.paymentSummary?.deliveryFee || 0).toFixed(2)}</s>
                          ) : (
                            <>Rs. {(order.paymentSummary?.deliveryFee || 0).toFixed(2)}</>
                          )}
                        </td>
                        <td>Rs. {(order.paymentSummary?.discount || 0).toFixed(2)}</td>
                        <td>Rs. {(order.paymentSummary?.netAmount || 0).toFixed(2)}</td>
                        <td>{getPaymentMethod(order)}</td>
                        <td>{(order as any).orderTakenBy?.displayName || ""}</td>
                        <td></td>
                      </tr>
                    ))}
                    <tr className="fw-bold">
                      <td colSpan={6} className="text-end">Total:</td>
                      <td>Rs. {filteredOrders.reduce((sum: number, o: any) => sum + (o.paymentSummary?.totalPrice || 0), 0).toFixed(2)}</td>
                      <td>Rs. {filteredOrders.reduce((sum: number, o: any) => sum + (o.paymentSummary?.isDeliveryFree ? 0 : (o.paymentSummary?.deliveryFee || 0)), 0).toFixed(2)}</td>
                      <td>Rs. {filteredOrders.reduce((sum: number, o: any) => sum + (o.paymentSummary?.discount || 0), 0).toFixed(2)}</td>
                      <td>Rs. {filteredOrders.reduce((sum: number, o: any) => sum + (o.paymentSummary?.netAmount || 0), 0).toFixed(2)}</td>
                      <td></td>
                      <td></td>
                      <td></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default DeliveryPrintSetupModal;